import { useState, useRef } from 'react'
import classnames from 'classnames/bind'
import { useTranslation } from 'next-i18next'
import * as Popover from '@radix-ui/react-popover'

// Libs
import useMedia from '@/hooks/useMedia'

// Components
import NotificationItem from '../../../Notification/components/NotificationItem'

// Types
import { MessageType } from '../../../MessageList'
import { NOTIFICATION_ACTION } from '@/constants/liveRoom'

// Styles
import styles from './style.module.scss'

const cx = classnames.bind(styles)

type MessageProps = MessageType & {
  className?: string
  isLarge?: boolean
  isLimitMessageOpen?: boolean
  setIsLimitMessageOpen?: any
  handleLimitClick?: any
  handleContextMenu?: any
  sender?: string
  canLimitMessage?: boolean
  silentUserList: string[]
  isHiddenMessage?: boolean
}

const CLICK_DURATION = 1500 // 長按時間，目前體感 1500 比較好

const Message = (props: MessageProps) => {
  const {
    className,
    name,
    context,
    type,
    isHost,
    isLarge,
    handleLimitClick,
    handleContextMenu,
    sender,
    canLimitMessage,
    silentUserList,
    isHiddenMessage,
  } = props

  const { t } = useTranslation()

  const [isLimitMessageOpen, setIsLimitMessageOpen] = useState(false)

  const messageItemRef = useRef<HTMLDivElement>(null)
  const messageNameRef: any = useRef(null)

  const { isMobile } = useMedia()

  const isChatMessage = type === 'chat'
  const isShareMessage = type === 'share'
  const isPlacardMessage = type === 'placard'
  const isNotificationMessage = type === NOTIFICATION_ACTION.USER_IN || type === NOTIFICATION_ACTION.PURCHASE

  const isLimited = silentUserList?.includes(sender as string)

  const handleRightClick = (event: any) => {
    if (!canLimitMessage) return

    handleContextMenu(event)

    setIsLimitMessageOpen(true)
  }

  let limitTimer: any = null

  const handleTouchStart = (event: any) => {
    if (!canLimitMessage) return

    limitTimer = setTimeout(() => {
      handleRightClick(event)
    }, CLICK_DURATION)
  }

  const handleTouchEnd = () => {
    clearTimeout(limitTimer)
  }

  return (
    <div ref={messageItemRef} className={cx('message', className, { isNotification: isNotificationMessage, isPlacard: isPlacardMessage })}>
      {/* 一般聊天 */}
      {isChatMessage && (
        <>
          {isHost && (
            <div className={cx('message-tag', { isHost, isLarge })}>
              <p className={cx('message-tag-text', { isLargeText: isLarge })}>{t('Common_Host')}</p>
            </div>
          )}
          {!isHost && (
            <Popover.Root open={isLimitMessageOpen} onOpenChange={(open) => setIsLimitMessageOpen(open)}>
              <Popover.Trigger
                className={cx('message-trigger')}
                data-can-limit={canLimitMessage}
                onClick={(event) => {
                  // 不想要觸發左鍵點擊
                  event.preventDefault()
                }}
              >
                <span
                  ref={messageNameRef}
                  className={cx('message-name', { isLargeText: isLarge })}
                  onTouchStart={handleTouchStart}
                  onTouchEnd={handleTouchEnd}
                  onContextMenu={handleRightClick}
                >
                  {name}
                </span>
              </Popover.Trigger>
              <Popover.Portal>
                <Popover.Content
                  className={cx('message-limit')}
                  side='top'
                  onClick={() => {
                    handleLimitClick({ name, token: sender, isLimited })
                    setIsLimitMessageOpen(false)
                  }}
                  onMouseLeave={() => setIsLimitMessageOpen(true)}
                >
                  <div className={cx('message-limit-text')}>{isLimited ? t('LiveRoom_Chat_Message_Unlimit') : t('LiveRoom_Chat_Message_Limit')}</div>
                  <Popover.Arrow />
                </Popover.Content>
              </Popover.Portal>
            </Popover.Root>
          )}
          <span className={cx('message-context', { isLargeText: isLarge, isHidden: isHiddenMessage })}>
            {isHiddenMessage ? '⛔️⛔️⛔️' : context}
          </span>
        </>
      )}

      {/* 分享訊息 */}
      {isShareMessage && (
        <>
          <span className={cx('message-name', { isLarge })}>{name || t('LiveRoom_Chat_Mystery_User')}</span>
          <span className={cx('message-context', { isLargeText: isLarge })}>{context}</span>
        </>
      )}
      {isPlacardMessage && (
        <div className={cx('message-placard', { isLarge })}>
          <div className={cx('message-tag', { isLarge })}>
            <p className={cx('message-tag-text', { isLargeText: isLarge })}>{t('LiveRoom_Placard')}</p>
          </div>
          <p className={cx('message-placard-content', { isLargeText: isLarge })}>{context}</p>
        </div>
      )}

      {!isMobile && isNotificationMessage && <NotificationItem {...props} />}
    </div>
  )
}

export default Message
